import { client } from '@/api/common';
import {
  API,
  BASE,
  URL_CATEGORIES,
  URL_TENANTS,
  V1,
} from '@/lib/constants/urls.ts';
import type { Category } from '@repo/database/browser';
import type {
  CreateCategoryDto,
  FetchCategoryDto,
  ListResponse,
  UpdateCategoryDto,
} from '@repo/api';
import type { AddNullToUndefined } from '@repo/utils';
import { CategoryResponse } from './models';

export const fetchCategory = async (
  tenantId: string,
  id?: string
): Promise<Category | undefined> => {
  const response = await client<Category>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}/${id}`,
    method: 'GET',
  });

  return response.data;
};

export const fetchCategories = async (
  tenantId: string,
  params?: FetchCategoryDto
): Promise<ListResponse<CategoryResponse>> => {
  const response = await client<ListResponse<Category>>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}`,
    method: 'GET',
    params,
  });
  return response.data;
};

export const fetchCategoriesTree = async (
  tenantId: string,
  params?: FetchCategoryDto
): Promise<ListResponse<Category>> => {
  const response = await client<ListResponse<Category>>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}`,
    method: 'GET',
    params: { ...params, includeChildren: 'true' },
  });
  return response.data;
};

export const createCategory = async (
  tenantId: string,
  params: AddNullToUndefined<CreateCategoryDto>
): Promise<Category> => {
  const response = await client<Category>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}`,
    method: 'POST',
    data: params,
  });
  return response.data;
};

export const updateCategory = async (
  tenantId: string,
  id: string,
  params: AddNullToUndefined<UpdateCategoryDto>
): Promise<Category> => {
  const response = await client<Category>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}/${id}`,
    method: 'PATCH',
    data: params,
  });
  return response.data;
};

export const archiveCategory = async (
  tenantId: string,
  id: string
): Promise<Category> => {
  const response = await client<Category>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}/${id}/archive`,
    method: 'POST',
  });
  return response.data;
};

export const restoreCategory = async (
  tenantId: string,
  id: string
): Promise<Category> => {
  const response = await client<Category>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}/${id}/restore`,
    method: 'POST',
  });
  return response.data;
};

export const deleteCategory = async (tenantId: string, id: string) => {
  const response = await client<Category>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_CATEGORIES}/${id}`,
    method: 'DELETE',
  });
  return response.data;
};
